import Edit from '@mui/icons-material/Edit'
import LocalOffer from '@mui/icons-material/LocalOffer'
import Add from '@mui/icons-material/Add'
import Business from '@mui/icons-material/Business'
import Person from '@mui/icons-material/Person'
import Place from '@mui/icons-material/Place'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import BaseAddCustomPriceComponent from 'src/mentor/components/MentorEventsPage/components/BaseAddCustomPriceComponent'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  getSPDataWithParams,
  postSPDataWithParams,
} from 'src/supportPortal/services/services'
import CopyButton from 'src/supportPortal/views/patients/components/CopyButton'
import { getMinimalMentorData } from 'src/supportPortal/views/therapists/util'

const SCOPE_PRACTITIONER = 'practitioner'
const SCOPE_CLINIC_LOCATION = 'clinic-location'
const SCOPE_CLINIC_BRAND = 'clinic-brand'

function formatCurrencyValue(bundlePack) {
  const amount = bundlePack?.finalPrice ?? bundlePack?.price
  if (amount == null) {
    return 'Pricing not set'
  }
  const currency = (bundlePack?.currency || '').toUpperCase()
  return `${currency ? `${currency} ` : ''}${amount}`
}

function buildBundleCountSummary(bundlePack) {
  const bundleItems = Array.isArray(bundlePack?.bundle) ? bundlePack.bundle : []
  if (!bundleItems.length) {
    return 'No bundled plans'
  }
  const countBasedUnits = bundleItems.reduce((sum, item) => sum + Number(item?.maxCount || 0), 0)
  if (countBasedUnits > 0) {
    return `${countBasedUnits} bundled uses across ${bundleItems.length} plan${bundleItems.length > 1 ? 's' : ''}`
  }
  return `${bundleItems.length} bundled plan${bundleItems.length > 1 ? 's' : ''}`
}

function formatPricingDetailLabel(countryCode, pricingDetail) {
  if (!pricingDetail) {
    return null
  }
  const regionLabel = countryCode?.toUpperCase() === 'ROW' ? 'ROW' : countryCode?.toUpperCase() || 'ROW'
  const currencyPrefix = pricingDetail?.currencySymbol || pricingDetail?.currencyId?.toUpperCase() || ''
  const originalPrice = pricingDetail?.price
  const discountedPrice = pricingDetail?.discountedPrice
  const introductoryPrice = pricingDetail?.introductoryPrice
  let label = `${regionLabel}: ${currencyPrefix ? `${currencyPrefix} ` : ''}${originalPrice ?? '-'}`
  if (discountedPrice != null && discountedPrice !== originalPrice) {
    label += ` -> ${discountedPrice}`
  }
  if (introductoryPrice != null && introductoryPrice !== discountedPrice) {
    label += ` (intro ${introductoryPrice})`
  }
  return label
}

function getPricingMatrixEntries(plan) {
  return Object.entries(plan?.pricingMatrix || {})
    .filter(([, pricingDetail]) => Boolean(pricingDetail))
    .sort(([leftCode], [rightCode]) => {
      if ((leftCode || '').toLowerCase() === 'row') {
        return -1
      }
      if ((rightCode || '').toLowerCase() === 'row') {
        return 1
      }
      return (leftCode || '').localeCompare(rightCode || '')
    })
}

function formatSourceTypeLabel(sourceType) {
  if (!sourceType) {
    return 'Eligible plan'
  }
  return sourceType
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function formatDurationSummary(durationsInMinutes) {
  return [...new Set((durationsInMinutes || []).filter(Boolean))]
    .map((duration) => `${duration} min`)
    .join(', ')
}

function formatDeliveryModeLabel(deliveryMode) {
  if (!deliveryMode) {
    return ''
  }
  const normalizedMode = `${deliveryMode}`.trim().toLowerCase()
  if (normalizedMode === 'aumhum' || normalizedMode === 'online' || normalizedMode === 'video-call') {
    return 'Online'
  }
  if (normalizedMode === 'offline' || normalizedMode === 'in-person') {
    return 'In Person'
  }
  if (normalizedMode === 'home-visit' || normalizedMode === 'home_visit') {
    return 'Home Visit'
  }
  return normalizedMode
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function buildBundlePlanOptionLabel(plan) {
  const eventTypeLabel = Array.isArray(plan?.eventTypeTitles) ? plan.eventTypeTitles.join(', ') : ''
  const durationLabel = formatDurationSummary(plan?.durationsInMinutes)
  const deliveryModeLabel = [...new Set((plan?.deliveryModes || []).filter(Boolean))]
    .map(formatDeliveryModeLabel)
    .filter(Boolean)
    .join(', ')
  const sourceLabel = Array.isArray(plan?.sourceTitles) ? plan.sourceTitles[0] : ''
  return [plan?.pricingPlanTitle || plan?.title, eventTypeLabel, durationLabel, deliveryModeLabel, sourceLabel]
    .filter(Boolean)
    .join(' • ')
}

export default function SupportBundlePackManagerPage({ scopeType }) {
  const navigate = useNavigate()
  const { mentorSlug, clinicSlug, brandSlug } = useParams()

  const [loadingContext, setLoadingContext] = useState(true)
  const [loadingBundles, setLoadingBundles] = useState(false)
  const [pageError, setPageError] = useState('')
  const [mentor, setMentor] = useState(null)
  const [clinicAssociations, setClinicAssociations] = useState([])
  const [selectedClinicId, setSelectedClinicId] = useState('')
  const [clinicLocation, setClinicLocation] = useState(null)
  const [clinicBrand, setClinicBrand] = useState(null)
  const [sourcePlans, setSourcePlans] = useState([])
  const [bundlePacks, setBundlePacks] = useState([])
  const [editorOpen, setEditorOpen] = useState(false)
  const [selectedBundlePack, setSelectedBundlePack] = useState(null)

  const selectedClinicAssociation = useMemo(
    () => clinicAssociations.find((association) => association?.id === selectedClinicId) || null,
    [clinicAssociations, selectedClinicId],
  )

  const scopeRequestParams = useMemo(() => {
    if (scopeType === SCOPE_PRACTITIONER) {
      if (!mentor?.id || !selectedClinicAssociation?.id || !selectedClinicAssociation?.clinicBrandId) {
        return null
      }
      return {
        scopeType: SCOPE_PRACTITIONER,
        'mentor-id': mentor.id,
        'clinic-brand-id': selectedClinicAssociation.clinicBrandId,
        'clinic-location-id': selectedClinicAssociation.id,
      }
    }
    if (scopeType === SCOPE_CLINIC_LOCATION) {
      if (!clinicLocation?.id || !clinicBrand?.id) {
        return null
      }
      return {
        scopeType: SCOPE_CLINIC_LOCATION,
        'clinic-brand-id': clinicBrand.id,
        'clinic-location-id': clinicLocation.id,
      }
    }
    if (!clinicBrand?.id) {
      return null
    }
    return {
      scopeType: SCOPE_CLINIC_BRAND,
      'clinic-brand-id': clinicBrand.id,
    }
  }, [scopeType, mentor?.id, selectedClinicAssociation, clinicLocation?.id, clinicBrand?.id])

  const bundlePlanOptions = useMemo(
    () =>
      (sourcePlans || []).map((plan) => ({
        ...plan,
        id: plan?.pricingPackId || plan?.id,
        title: buildBundlePlanOptionLabel(plan),
      })),
    [sourcePlans],
  )

  const pricingCountryCode = useMemo(() => {
    return (
      mentor?.location?.countryCode ||
      selectedClinicAssociation?.countryCode ||
      selectedClinicAssociation?.location?.countryCode ||
      clinicLocation?.countryCode ||
      clinicLocation?.location?.countryCode ||
      clinicBrand?.countryCode ||
      clinicBrand?.location?.countryCode ||
      'IN'
    )
  }, [mentor, selectedClinicAssociation, clinicLocation, clinicBrand])

  const pricingProfileData = useMemo(
    () => ({ location: { countryCode: pricingCountryCode } }),
    [pricingCountryCode],
  )

  const pageHeading = useMemo(() => {
    if (scopeType === SCOPE_PRACTITIONER) {
      return `${mentor?.name || 'Practitioner'} Bundle Packs`
    }
    if (scopeType === SCOPE_CLINIC_LOCATION) {
      const brandName = clinicBrand?.brandName || clinicBrand?.title || ''
      const locationName = clinicLocation?.title || clinicLocation?.name || ''
      return `${brandName}${brandName && locationName ? ' - ' : ''}${locationName} Bundle Packs`
    }
    return `${clinicBrand?.brandName || clinicBrand?.title || 'Clinic Brand'} Brand Bundle Packs`
  }, [scopeType, mentor, clinicLocation, clinicBrand])

  const pageSubHeading = useMemo(() => {
    if (scopeType === SCOPE_PRACTITIONER) {
      return 'Bundle practitioner events and clinic appointments for one clinic scope, then price them with a discount.'
    }
    if (scopeType === SCOPE_CLINIC_LOCATION) {
      return 'Bundle clinic appointment types available at this location.'
    }
    return 'Bundle clinic-brand appointment types that are defined without a clinic location.'
  }, [scopeType])

  const sourcePlansEmptyMessage = useMemo(() => {
    if (scopeType === SCOPE_PRACTITIONER) {
      return 'No eligible appointment pricing plans are available for this practitioner in the selected clinic scope. Only event types backed by enabled clinical services are shown here.'
    }
    return 'No eligible appointment pricing plans were found for this scope yet.'
  }, [scopeType])

  async function loadBundleData(params) {
    if (!params) {
      setSourcePlans([])
      setBundlePacks([])
      return
    }
    setLoadingBundles(true)
    setPageError('')
    try {
      const [sourcePlansResp, bundlePacksResp] = await Promise.all([
        getSPDataWithParams('/api/secure/cc/support/bundle-pack/source-plans', params),
        getSPDataWithParams('/api/secure/cc/support/bundle-pack/list', params),
      ])

      if (!isCommonResponseSuccessfulV2(sourcePlansResp) || !isCommonResponseSuccessfulV2(bundlePacksResp)) {
        setPageError('Unable to load bundle-pack data for this scope right now.')
        return
      }

      const sourcePlansData = getDataMapFromCommonResponse(sourcePlansResp)?.sourcePlans || []
      const bundlePacksData =
        getDataMapFromCommonResponse(bundlePacksResp)?.['plan-bundle-pack'] || []
      setSourcePlans(sourcePlansData)
      setBundlePacks(bundlePacksData)
    } catch (error) {
      setPageError('Unable to load bundle-pack data for this scope right now.')
    } finally {
      setLoadingBundles(false)
    }
  }

  async function loadPractitionerContext() {
    const [mentorData, clinicResp] = await Promise.all([
      getMinimalMentorData(mentorSlug, true),
      getSPDataWithParams('/api/secure/cc/support/therapists/clinic-associations', {
        'mentor-slug': mentorSlug,
      }),
    ])

    if (!mentorData) {
      navigate('/support-home/practitioner-management/therapists')
      return
    }

    setMentor(mentorData)

    if (!isCommonResponseSuccessfulV2(clinicResp)) {
      setClinicAssociations([])
      setSelectedClinicId('')
      return
    }

    const associations = getDataMapFromCommonResponse(clinicResp)?.clinic || []
    setClinicAssociations(associations)
    const storedClinic = sessionStorage.getItem('supportClinicData')
    let defaultClinicId = associations?.[0]?.id || ''
    if (storedClinic) {
      try {
        const parsedClinic = JSON.parse(storedClinic)
        if (associations.some((association) => association?.id === parsedClinic?.id)) {
          defaultClinicId = parsedClinic?.id
        }
      } catch (error) {
        // ignore invalid session data
      }
    }
    setSelectedClinicId(defaultClinicId)
  }

  async function loadClinicLocationContext() {
    const clinicResp = await getSPDataWithParams('/api/secure/cc/support/clinic/by-slug', {
      'clinic-slug': clinicSlug,
      sendBrandDetails: true,
    })

    if (!isCommonResponseSuccessfulV2(clinicResp)) {
      navigate('/support-home/clinics-hospitals/clinics')
      return
    }

    const data = getDataMapFromCommonResponse(clinicResp)
    const locationData = data?.clinic || null
    const brandData = data?.['clinic-brand'] || null
    setClinicLocation(locationData)
    setClinicBrand(brandData)
  }

  async function loadClinicBrandContext() {
    const brandResp = await getSPDataWithParams('/api/secure/cc/support/clinic-brand/by-slug', {
      'clinic-brand-slug': brandSlug,
    })

    if (!isCommonResponseSuccessfulV2(brandResp)) {
      navigate('/support-home/clinics-hospitals/clinics')
      return
    }

    const brandData = getDataMapFromCommonResponse(brandResp)?.['clinic-brand'] || null
    setClinicBrand(brandData)
  }

  useEffect(() => {
    let cancelled = false
    async function loadContext() {
      setLoadingContext(true)
      setPageError('')
      try {
        if (scopeType === SCOPE_PRACTITIONER) {
          await loadPractitionerContext()
        } else if (scopeType === SCOPE_CLINIC_LOCATION) {
          await loadClinicLocationContext()
        } else {
          await loadClinicBrandContext()
        }
      } catch (error) {
        if (!cancelled) {
          setPageError('Unable to load this bundle-pack page right now.')
        }
      } finally {
        if (!cancelled) {
          setLoadingContext(false)
        }
      }
    }

    loadContext()
    return () => {
      cancelled = true
    }
  }, [scopeType, mentorSlug, clinicSlug, brandSlug])

  useEffect(() => {
    loadBundleData(scopeRequestParams)
  }, [scopeRequestParams ? JSON.stringify(scopeRequestParams) : ''])

  function openCreateDialog() {
    setSelectedBundlePack(null)
    setEditorOpen(true)
  }

  function openEditDialog(bundlePack) {
    setSelectedBundlePack(bundlePack)
    setEditorOpen(true)
  }

  async function saveBundlePack(bundlePackPayload) {
    return postSPDataWithParams(
      '/api/secure/cc/support/bundle-pack/save',
      bundlePackPayload,
      scopeRequestParams || {},
    )
  }

  return (
    <>
      <SupportHeader heading={pageHeading} subHeading={pageSubHeading} showBackButton />
      <Box sx={{ p: { xs: 2, md: 3 }, background: '#f7f8fb', minHeight: 'calc(100vh - 140px)' }}>
        {loadingContext ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {pageError ? <Alert severity="error">{pageError}</Alert> : null}

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Scope Details
                      </Typography>
                      <Typography color="text.secondary">{pageSubHeading}</Typography>
                    </Box>
                    <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog} disabled={!scopeRequestParams || loadingBundles || bundlePlanOptions.length === 0}>
                      New Bundle Pack
                    </Button>
                  </Stack>

                  {scopeType === SCOPE_PRACTITIONER ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Person color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Practitioner</Typography>
                            <Typography sx={{ fontWeight: 600 }}>{mentor?.name || 'Unknown'}</Typography>
                          </Box>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <FormControl fullWidth>
                          <InputLabel id="bundle-pack-clinic-scope-label">Clinic Scope</InputLabel>
                          <Select
                            labelId="bundle-pack-clinic-scope-label"
                            label="Clinic Scope"
                            value={selectedClinicId}
                            onChange={(event) => setSelectedClinicId(event.target.value)}
                          >
                            {clinicAssociations.map((association) => (
                              <MenuItem key={association?.id} value={association?.id}>
                                {association?.clinicBrandName || association?.clinicBrandSlug || 'Clinic Brand'} - {association?.title}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Business color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Clinic Brand</Typography>
                            <Typography sx={{ fontWeight: 600 }}>{clinicBrand?.brandName || clinicBrand?.title || 'Unknown'}</Typography>
                          </Box>
                        </Stack>
                      </Grid>
                      {scopeType === SCOPE_CLINIC_LOCATION ? (
                        <Grid item xs={12} md={6}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Place color="action" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Clinic Location</Typography>
                              <Typography sx={{ fontWeight: 600 }}>{clinicLocation?.title || clinicLocation?.name || 'Unknown'}</Typography>
                            </Box>
                          </Stack>
                        </Grid>
                      ) : null}
                    </Grid>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Plans You Can Add To This Bundle
                    </Typography>
                    <Typography color="text.secondary">
                      These are the appointment pricing plans you can include in this booking pack. Use this list to understand the service type, duration, delivery mode, and pricing before selecting plans in the editor.
                    </Typography>
                  </Box>
                  {!scopeRequestParams ? (
                    <Alert severity="info">Select a valid scope to load eligible appointment pricing plans.</Alert>
                  ) : loadingBundles ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : bundlePlanOptions.length === 0 ? (
                    <Alert severity="warning">{sourcePlansEmptyMessage}</Alert>
                  ) : (
                    <Grid container spacing={2}>
                      {sourcePlans.map((plan) => (
                        <Grid item xs={12} md={6} lg={4} key={plan?.id}>
                          <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                              <Stack spacing={1.5} sx={{ height: '100%' }}>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                  <LocalOffer fontSize="small" color="action" />
                                  <Box>
                                    <Typography sx={{ fontWeight: 700 }}>
                                      {plan?.pricingPlanTitle || plan?.title}
                                    </Typography>
                                    {plan?.pricingSummary ? (
                                      <Typography variant="body2" color="text.secondary">
                                        {plan.pricingSummary}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </Stack>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  {(plan?.eventTypeTitles || []).map((eventTypeTitle) => (
                                    <Chip
                                      key={`${plan?.id}-${eventTypeTitle}`}
                                      label={eventTypeTitle}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  ))}
                                  <Chip
                                    label={formatSourceTypeLabel(plan?.sourceType)}
                                    size="small"
                                    sx={{ width: 'fit-content' }}
                                  />
                                  {formatDurationSummary(plan?.durationsInMinutes) ? (
                                    <Chip
                                      label={formatDurationSummary(plan?.durationsInMinutes)}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ) : null}
                                  {[...new Set((plan?.deliveryModes || []).filter(Boolean))]
                                    .map(formatDeliveryModeLabel)
                                    .filter(Boolean)
                                    .map((deliveryMode) => (
                                      <Chip
                                        key={`${plan?.id}-${deliveryMode}`}
                                        label={deliveryMode}
                                        size="small"
                                        variant="outlined"
                                      />
                                    ))}
                                </Stack>
                                {Array.isArray(plan?.sourceTitles) && plan.sourceTitles.length > 0 ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Appointment titles: {plan.sourceTitles.join(', ')}
                                  </Typography>
                                ) : null}
                                {getPricingMatrixEntries(plan).length > 0 ? (
                                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {getPricingMatrixEntries(plan).map(([countryCode, pricingDetail]) => (
                                      <Chip
                                        key={`${plan?.id}-${countryCode}`}
                                        size="small"
                                        label={formatPricingDetailLabel(countryCode, pricingDetail)}
                                      />
                                    ))}
                                  </Stack>
                                ) : null}
                                <Box sx={{ flexGrow: 1 }} />
                                <CopyButton
                                  label="Pricing Plan ID"
                                  value={plan?.pricingPackId}
                                  buttonLabel="Copy Pricing Plan ID"
                                  buttonSize="small"
                                />
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Existing Bundle Packs
                      </Typography>
                      <Typography color="text.secondary">
                        Edit prices, included plan counts, and scope-specific bundle definitions from here.
                      </Typography>
                    </Box>
                    <Chip label={`${bundlePacks.length} pack${bundlePacks.length === 1 ? '' : 's'}`} />
                  </Stack>
                  {loadingBundles ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : bundlePacks.length === 0 ? (
                    <Alert severity="info">No bundle packs have been created for this scope yet.</Alert>
                  ) : (
                    <Grid container spacing={2}>
                      {bundlePacks.map((bundlePack) => (
                        <Grid item xs={12} md={6} lg={4} key={bundlePack?.id}>
                          <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                              <Stack spacing={1.5} sx={{ height: '100%' }}>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                  <Typography sx={{ fontWeight: 700 }}>{bundlePack?.title}</Typography>
                                  <Chip
                                    size="small"
                                    label={bundlePack?.enabled ? 'Enabled' : 'Disabled'}
                                    color={bundlePack?.enabled ? 'success' : 'default'}
                                  />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                  {buildBundleCountSummary(bundlePack)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Bundle ID: {bundlePack?.id}
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  <Chip size="small" label={formatCurrencyValue(bundlePack)} color="primary" />
                                  {bundlePack?.price != null && bundlePack?.finalPrice != null && bundlePack?.price !== bundlePack?.finalPrice ? (
                                    <Chip size="small" label={`Discount ${bundlePack?.price - bundlePack?.finalPrice}`} />
                                  ) : null}
                                </Stack>
                                <Box sx={{ flexGrow: 1 }} />
                                <Button
                                  variant="outlined"
                                  startIcon={<Edit />}
                                  onClick={() => openEditDialog(bundlePack)}
                                >
                                  Edit
                                </Button>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}
      </Box>

      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} fullWidth maxWidth="lg">
        <BaseAddCustomPriceComponent
          selectedPlan={selectedBundlePack}
          setSelectedPlan={setSelectedBundlePack}
          handleChangePlan={() => {}}
          closeFunction={() => {
            setEditorOpen(false)
            setSelectedBundlePack(null)
          }}
          refreshOneOnOnePlans={() => loadBundleData(scopeRequestParams)}
          type="bundle"
          mentorProfileData={pricingProfileData}
          bundleEditorMode="legacy"
          bundlePlanOptions={bundlePlanOptions}
          saveBundlePackRequestFn={saveBundlePack}
        />
      </Dialog>
    </>
  )
}
